<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { markTourSeen } from './tourStorage';

// Squareme's one in-app product tour: a short guided walk through a screen.
// Each step points at an element marked with data-tour="<target>"; a step
// whose element is not on screen is shown centred instead of being skipped.
// A step may carry `before` (sync or async), run before it is shown, e.g. to
// switch to the tab its target lives on. Open it with v-model; finishing or
// skipping remembers it per browser (see tourStorage) so callers can decide
// whether to start it on their own with tourSeen(storageKey).
const props = defineProps({
  steps: {
    type: Array,
    required: true,
    validator: steps => steps.every(step => step.target && step.title),
  },
  storageKey: { type: String, required: true },
});

const emit = defineEmits(['step', 'finish']);
const open = defineModel({ type: Boolean, default: false });
const { t } = useI18n();
const index = ref(0);
const rect = ref(null);
const card = ref(null);
const step = computed(() => props.steps[index.value] || {});
const isLast = computed(() => index.value === props.steps.length - 1);
const titleId = `tour-title-${props.storageKey.replace(/[^\w-]/g, '-')}`;

const measure = () => {
  const element = document.querySelector(`[data-tour="${step.value.target}"]`);
  element?.scrollIntoView?.({ block: 'center' });
  const box = element?.getBoundingClientRect();
  rect.value = box && box.width > 0 ? box : null;
};

// Geometry comes from the page, so these two are computed styles by necessity.
const highlightStyle = computed(() => {
  if (!rect.value) return null;
  const pad = 6;
  return {
    top: `${rect.value.top - pad}px`,
    left: `${rect.value.left - pad}px`,
    width: `${rect.value.width + pad * 2}px`,
    height: `${rect.value.height + pad * 2}px`,
  };
});
const cardStyle = computed(() => {
  if (!rect.value) return null;
  const width = 320;
  const margin = 12;
  const left = Math.min(
    Math.max(margin, rect.value.left + rect.value.width / 2 - width / 2),
    window.innerWidth - width - margin
  );
  const below = rect.value.bottom + margin;
  const fitsBelow = below + 200 < window.innerHeight;
  return fitsBelow
    ? { top: `${below}px`, left: `${left}px` }
    : {
        bottom: `${window.innerHeight - rect.value.top + margin}px`,
        left: `${left}px`,
      };
});

const show = async i => {
  index.value = i;
  await step.value.before?.();
  emit('step', step.value);
  await nextTick();
  measure();
  card.value?.focus();
};

const finish = () => {
  markTourSeen(props.storageKey);
  open.value = false;
  emit('finish');
};
const go = delta => {
  if (delta > 0 && isLast.value) {
    finish();
    return null;
  }
  return show(
    Math.max(0, Math.min(props.steps.length - 1, index.value + delta))
  );
};
const onKey = event => {
  if (event.key === 'Escape') finish();
  if (event.key === 'ArrowRight') go(1);
  if (event.key === 'ArrowLeft') go(-1);
};

watch(
  open,
  async value => {
    window.removeEventListener('resize', measure);
    if (!value) return;
    window.addEventListener('resize', measure);
    await show(0);
  },
  { immediate: true }
);
onBeforeUnmount(() => window.removeEventListener('resize', measure));
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[60]"
      data-test-id="product-tour"
      @keydown="onKey"
    >
      <div class="absolute inset-0 bg-n-alpha-black1" @click="finish" />
      <div
        v-if="highlightStyle"
        class="fixed rounded-xl ring-2 ring-n-brand pointer-events-none transition-all duration-200"
        :style="highlightStyle"
      />
      <div
        ref="card"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        class="fixed w-80 max-w-[calc(100vw-24px)] rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl p-4 flex flex-col gap-3 outline-none"
        :class="cardStyle ? '' : 'top-1/3 left-1/2 -translate-x-1/2'"
        :style="cardStyle"
      >
        <p class="text-xs text-n-slate-10 tabular-nums">
          {{
            t('PRODUCT_TOUR.STEP', {
              current: index + 1,
              total: steps.length,
            })
          }}
        </p>
        <h3 :id="titleId" class="text-base font-semibold text-n-slate-12">
          {{ step.title }}
        </h3>
        <p class="text-sm text-n-slate-11 leading-relaxed">{{ step.body }}</p>
        <div class="flex items-center justify-between gap-2 pt-1">
          <Button
            :label="t('PRODUCT_TOUR.SKIP')"
            variant="link"
            color="slate"
            size="sm"
            @click="finish"
          />
          <div class="flex gap-2">
            <Button
              v-if="index > 0"
              :label="t('PRODUCT_TOUR.BACK')"
              variant="faded"
              color="slate"
              size="sm"
              @click="go(-1)"
            />
            <Button
              :label="isLast ? t('PRODUCT_TOUR.DONE') : t('PRODUCT_TOUR.NEXT')"
              size="sm"
              data-test-id="tour-next"
              @click="go(1)"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
